import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useWindowWidth } from '@react-hook/window-size';
import { useMediaQuery } from 'react-responsive';
import i18next from 'i18next';
import { useTranslation } from 'react-i18next';
import { produce } from 'immer';
import type { IHighlight } from 'react-pdf-highlighter';
import Player from 'video.js/dist/types/player';

import { useLoadAssets } from './useLoadAssets';
import { useSourceHandlers } from './useSourceHandlers';
import { useDerivedTheme } from './useDerivedTheme';
import { getDefaultTheme } from './useDefaultTheme';
import { useCustomStyles } from './useCustomStyles';
import {
  getIsEmbed,
  hasCustomStyles,
  hasDocumentAnimation,
  shouldShowLogo,
  getFullLocale,
} from '../helpers';

import {
  CallToActions,
  Cuepoint,
  CustomTheme,
  Layout,
  MediaProjectInstanceResponse,
  MessageOrigin,
  PipVideoSize,
  ProjectDocument,
  PublicationIdentifiers,
  ResponsiveAppProps,
} from '../types';

const DEFAULT_LANGUAGE = 'en';

export interface UseResponsiveAppStateResult {
  // Publication data
  publication: MediaProjectInstanceResponse | undefined;
  projectId: string;
  publicationId: string;
  mediaUrl: string | undefined;
  posterUrl: string | undefined;
  mainDocument: ProjectDocument | undefined;
  organizationId: string | undefined;
  documentIds: string[];
  showDocumentAnimation: boolean;

  // UI state
  layout: Layout;
  setLayout: (layout: Layout) => void;
  firstQuery: string | undefined;
  setFirstQuery: (query: string | undefined) => void;
  nextQuery: string | undefined;
  setNextQuery: (query: string | undefined) => void;
  locale: string;
  changeLocale: (lang: string) => Promise<void>;
  ready: boolean;
  suggestions: string[] | undefined;
  setSuggestions: (suggestions: string[] | undefined) => void;
  openChat: boolean;
  setOpenChat: React.Dispatch<React.SetStateAction<boolean>>;
  chatOpen: boolean;
  setChatOpen: (open: boolean) => void;
  chatHeight: number;
  setChatHeight: (height: number) => void;
  showMobilePlayButton: boolean;
  setShowMobilePlayButton: React.Dispatch<React.SetStateAction<boolean>>;
  showPauseOverlay: boolean;
  setShowPauseOverlay: React.Dispatch<React.SetStateAction<boolean>>;
  isVideoLoaded: boolean;
  setIsVideoLoaded: React.Dispatch<React.SetStateAction<boolean>>;
  rerenderCount: number;
  setRerenderCount: React.Dispatch<React.SetStateAction<number>>;
  // UI params
  isEmbed: boolean;
  showTitle: boolean;
  showLogo: boolean;
  isTransparent: boolean;
  showSideCtas: boolean;
  pipVideoSize: PipVideoSize;
  queryParams: URLSearchParams;
  mode: string | null;

  // Theme
  theme: CustomTheme;

  // Assets
  cuepoints: Cuepoint[];
  setCuepoints: React.Dispatch<React.SetStateAction<Cuepoint[]>>;
  highlights: IHighlight[];
  setHighlights: React.Dispatch<React.SetStateAction<IHighlight[]>>;
  sections: { title: string; startTime: number }[];
  callToActions: CallToActions | undefined;
  cleanHighlightAndCuepoints: () => void;
  loadAssets: () => Promise<boolean>;

  // Source handlers
  handleDisplaySource: (source: MessageOrigin) => void;

  // Refs
  playerRef: React.MutableRefObject<Player | null>;
  pdfViewerRef: React.MutableRefObject<never>;

  // Responsive
  isMobile: boolean;
  windowWidth: number;

  // Translation
  t: (key: string) => string;
  appContextValue: {
    t: (key: string) => string;
    identifiers: PublicationIdentifiers;
  };

  // Identifiers (pass-through)
  organizationSlug?: string;
  instanceSlug?: string;
}

/**
 * Main hook for ResponsiveApp state management
 * Consolidates all shared state, effects, and handlers between ResponsiveApp variants
 */
export const useResponsiveAppState = ({
  params,
  headers,
  publication: publicationProp,
  organizationId: organizationIdProp,
  organizationSlug,
  instanceSlug,
}: ResponsiveAppProps): UseResponsiveAppStateResult => {
  // Separate publication data from error
  const publication = (
    publicationProp && 'instance_id' in publicationProp
      ? publicationProp
      : undefined
  ) as MediaProjectInstanceResponse | undefined;

  const projectId = publication?.project_id ?? '';
  const publicationId = publication?.instance_id ?? '';
  const queryParams = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : new URLSearchParams();
  const mode = queryParams.get('__mode');

  // Core UI state
  const [layout, setLayout] = useState<Layout>('video');
  const [firstQuery, setFirstQuery] = useState<string>();
  const [nextQuery, setNextQuery] = useState<string>();
  const [ready, setReady] = useState(false);
  const [suggestions, setSuggestions] = useState<string[]>();
  const [openChat, setOpenChat] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatHeight, setChatHeight] = useState(45);
  const [showMobilePlayButton, setShowMobilePlayButton] = useState(true);
  const [showPauseOverlay, setShowPauseOverlay] = useState(false);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const [rerenderCount, setRerenderCount] = useState(0);

  // Locale state
  const [locale, setLocale] = useState(() =>
    getFullLocale(i18next.resolvedLanguage ?? DEFAULT_LANGUAGE),
  );

  const changeLocale = useCallback(async (lang: string) => {
    await i18next.changeLanguage(lang.slice(0, 2));
    setLocale(lang);
  }, []);

  // UI params from query
  const isEmbed = getIsEmbed(params, queryParams.get('embed'));
  const showTitle = queryParams.get('title') === '1';
  const showLogo =
    shouldShowLogo(publication?.project_id) && queryParams.get('logo') !== '0';
  const isTransparent = queryParams.get('transparent')
    ? queryParams.get('transparent') === '1'
    : isEmbed;
  const showSideCtas = queryParams.get('sideactions') !== '0';
  const pipVideoSize: PipVideoSize =
    queryParams.get('pip_vsize') === 'large' ? 'large' : 'small';

  // Publication data
  const mediaUrl = publication?.media?.url;
  const rawThumbnail = publication?.media?.metadata?.['thumbnail'];
  const posterUrl =
    typeof rawThumbnail === 'string' && rawThumbnail.length > 0
      ? rawThumbnail
      : undefined;
  const mainDocument = publication?.documents?.Document_0;
  const organizationId = organizationIdProp ?? publication?.media?.organization;
  const documentIds = Object.values(publication?.documents ?? []).map(
    (x) => x.id,
  );
  const showDocumentAnimation = hasDocumentAnimation(publication);

  // Refs
  const playerRef = useRef<Player | null>(null);
  const pdfViewerRef = useRef<never>(null as never);

  // Responsive
  const isMobile = useMediaQuery({ query: '(max-width: 767px)' });
  const windowWidth = useWindowWidth();

  // Custom styles
  const { loadCustomStyles } = useCustomStyles();

  // Theme setup
  const [baseTheme, setBaseTheme] = useState(getDefaultTheme());

  useEffect(() => {
    const orgId = organizationId;
    if (orgId && hasCustomStyles(orgId)) {
      loadCustomStyles(
        `/data/organizations/${orgId}/default.css`,
        (customTheme) => {
          setBaseTheme(
            produce((draft) => {
              if (customTheme.token.colorPrimary) {
                draft.colors.primary = customTheme.token.colorPrimary;
              }
              if (customTheme.token.fontFamily) {
                draft.fontFamily = customTheme.token.fontFamily;
              }
            }),
          );
        },
      );
    }
  }, [loadCustomStyles, organizationId]);

  const theme = useDerivedTheme(baseTheme, publication);

  // Assets
  const {
    cuepoints,
    setCuepoints,
    highlights,
    setHighlights,
    sections,
    callToActions,
    loadAssets,
    cleanHighlightAndCuepoints,
  } = useLoadAssets(publication);

  // Source handlers
  const { handleDisplaySource } = useSourceHandlers({
    sourceHighlightColor: theme.colors.primary,
    documentIds,
    setHighlights,
    setCuepoints,
    pdfViewerRef,
  });

  // Body class effects
  useEffect(() => {
    if (isEmbed) {
      document.body.classList.add('embed');
    }
  }, [isEmbed]);

  useEffect(() => {
    if (isTransparent) {
      document.body.classList.add('transparent');
    }
  }, [isTransparent]);

  useEffect(() => {
    if (showDocumentAnimation) {
      document.body.classList.add('has-document');
    }
  }, [showDocumentAnimation]);

  // Prevent page zooming
  useEffect(() => {
    document.addEventListener('gesturestart', function (e) {
      e.preventDefault();
    });
  }, []);

  // Ready state initialization
  useEffect(() => {
    (async () => {
      if (!publication?.media?.url) {
        return;
      }
      await loadAssets();
      setReady(true);
    })();
  }, [publication, loadAssets]);

  // Debug: expose publication to window
  useEffect(() => {
    if (publication && 'instance_id' in publication) {
      (globalThis.window as any).__libertify = { publication };
    }
  }, [publication]);

  // Translation — disable Suspense so SSR doesn't throw a promise
  const { t } = useTranslation('translation', { useSuspense: false });

  // App context value
  const appContextValue = useMemo(
    () => ({
      t,
      identifiers: {
        projectId,
        publicationId,
        organizationSlug,
        instanceSlug,
        organizationId,
      },
    }),
    [t, projectId, publicationId, organizationSlug, instanceSlug, organizationId],
  );

  return {
    // Publication data
    publication,
    projectId,
    publicationId,
    mediaUrl: mediaUrl ?? undefined,
    posterUrl,
    mainDocument,
    organizationId,
    documentIds,
    showDocumentAnimation,

    // UI state
    layout,
    setLayout,
    firstQuery,
    setFirstQuery,
    nextQuery,
    setNextQuery,
    locale,
    changeLocale,
    ready,
    suggestions,
    setSuggestions,
    openChat,
    setOpenChat,
    chatOpen,
    setChatOpen,
    chatHeight,
    setChatHeight,
    showMobilePlayButton,
    setShowMobilePlayButton,
    showPauseOverlay,
    setShowPauseOverlay,
    isVideoLoaded,
    setIsVideoLoaded,
    rerenderCount,
    setRerenderCount,
    // UI params
    isEmbed,
    showTitle,
    showLogo,
    isTransparent,
    showSideCtas,
    pipVideoSize,
    queryParams,
    mode,

    // Theme
    theme,

    // Assets
    cuepoints,
    setCuepoints,
    highlights,
    setHighlights,
    sections,
    callToActions,
    cleanHighlightAndCuepoints,
    loadAssets,

    // Source handlers
    handleDisplaySource,

    // Refs
    playerRef,
    pdfViewerRef,

    // Responsive
    isMobile,
    windowWidth,

    // Translation
    t,
    appContextValue,

    // Identifiers
    organizationSlug,
    instanceSlug,
  };
};
